import React, { useState } from 'react';
import { X, Upload, FileText, Calendar, Image } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Categorie documenti per Asset
const ASSET_DOC_CATEGORIES = [
  { value: 'libretto_uso_manutenzione', label: 'Libretto uso e manutenzione' },
  { value: 'certificato_conformita', label: 'Certificato conformità' },
  { value: 'revisione_periodica', label: 'Revisione/Controllo periodico' },
  { value: 'documento_consegna', label: 'Documento di consegna' },
  { value: 'fattura_ddt', label: 'Fattura/DDT acquisto' },
  { value: 'assicurazione', label: 'Assicurazione' },
  { value: 'collaudo', label: 'Collaudo' },
  { value: 'foto_asset', label: 'Foto asset' },
  { value: 'altro', label: 'Altro' }
];

function AssetDocumentModal({ assetId, onClose }) {
  const [formData, setFormData] = useState({
    categoria: 'libretto_uso_manutenzione',
    titolo: '',
    descrizione: '',
    data_rilascio: '',
    data_scadenza: '',
    numero_protocollo: ''
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Check file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(selectedFile.type)) {
        setError('Solo file PDF, JPG o PNG sono permessi');
        return;
      }
      // Check file size (12MB max)
      if (selectedFile.size > 12 * 1024 * 1024) {
        setError('Il file non può superare i 12MB');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.titolo) {
      setError('Il titolo è obbligatorio');
      return;
    }

    if (!file) {
      setError('Devi caricare un file');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setUploadProgress(25);

      // Create compliance item for asset maintenance/docs
      const compliancePayload = {
        tipo: 'documento_asset',
        titolo: formData.titolo,
        descrizione: formData.descrizione,
        employee_id: 'asset_' + assetId, // Special marker for asset docs
        data_rilascio: formData.data_rilascio ? new Date(formData.data_rilascio).toISOString() : null,
        data_scadenza: formData.data_scadenza ? new Date(formData.data_scadenza).toISOString() : new Date('2099-12-31').toISOString(),
        stato: 'attivo',
        categoria: formData.categoria,
        numero_protocollo: formData.numero_protocollo
      };

      setUploadProgress(50);

      const token = localStorage.getItem('token');

      const complianceRes = await fetch(`${API_URL}/api/compliance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(compliancePayload)
      });

      if (!complianceRes.ok) {
        throw new Error('Errore nella creazione del documento');
      }

      const complianceData = await complianceRes.json();
      setUploadProgress(75);

      // Upload file
      const fileFormData = new FormData();
      fileFormData.append('file', file);
      fileFormData.append('entity_type', 'asset');
      fileFormData.append('entity_id', assetId);
      fileFormData.append('storage_type', 'filesystem');
      fileFormData.append('categoria', formData.categoria);
      fileFormData.append('descrizione', formData.titolo);

      const uploadRes = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: fileFormData
      });

      if (!uploadRes.ok) {
        throw new Error('Errore nel caricamento del file');
      }

      setUploadProgress(100);
      onClose(true);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const isPhotoCategory = formData.categoria === 'foto_asset';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="asset-document-modal">
      <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            {isPhotoCategory ? <Image size={24} className="text-primary" /> : <FileText size={24} className="text-primary" />}
            Carica {isPhotoCategory ? 'Foto' : 'Documento'} Asset
          </h2>
          <button
            onClick={() => onClose(false)}
            className="btn btn-secondary"
            data-testid="close-modal"
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Caricamento in corso...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Tipo Documento *</label>
              <select
                name="categoria"
                className="form-input"
                value={formData.categoria}
                onChange={handleChange}
                required
                data-testid="input-categoria"
              >
                {ASSET_DOC_CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Titolo *</label>
              <input
                type="text"
                name="titolo"
                className="form-input"
                value={formData.titolo}
                onChange={handleChange}
                required
                placeholder={isPhotoCategory ? "Es: Foto frontale furgone" : "Es: Libretto uso e manutenzione"}
                data-testid="input-titolo"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Descrizione</label>
              <textarea
                name="descrizione"
                className="form-input form-textarea"
                value={formData.descrizione}
                onChange={handleChange}
                rows={3}
                placeholder="Inserisci dettagli aggiuntivi..."
                data-testid="input-descrizione"
              />
            </div>

            {!isPhotoCategory && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label flex items-center gap-2">
                    <Calendar size={16} />
                    Data Documento
                  </label>
                  <input
                    type="date"
                    name="data_rilascio"
                    className="form-input"
                    value={formData.data_rilascio}
                    onChange={handleChange}
                    data-testid="input-data-rilascio"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label flex items-center gap-2">
                    <Calendar size={16} />
                    Data Scadenza (se applicabile)
                  </label>
                  <input
                    type="date"
                    name="data_scadenza"
                    className="form-input"
                    value={formData.data_scadenza}
                    onChange={handleChange}
                    data-testid="input-data-scadenza"
                  />
                </div>
              </div>
            )}

            {!isPhotoCategory && (
              <div className="form-group">
                <label className="form-label">Numero Protocollo/Riferimento</label>
                <input
                  type="text"
                  name="numero_protocollo"
                  className="form-input font-mono"
                  value={formData.numero_protocollo}
                  onChange={handleChange}
                  placeholder="Es: REV-2026-001"
                  data-testid="input-numero-protocollo"
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label flex items-center gap-2">
                <Upload size={16} />
                Carica File *
              </label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors">
                <input
                  type="file"
                  accept={isPhotoCategory ? "image/*" : ".pdf,image/*"}
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  data-testid="file-input"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  {isPhotoCategory ? (
                    <Image size={48} className="text-muted-foreground" />
                  ) : (
                    <FileText size={48} className="text-muted-foreground" />
                  )}
                  {file ? (
                    <>
                      <p className="font-medium text-primary">{file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium">Clicca per selezionare un file</p>
                      <p className="text-sm text-muted-foreground">
                        {isPhotoCategory ? "JPG, PNG - Max 12MB" : "PDF, JPG o PNG - Max 12MB"}
                      </p>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-end mt-6">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="btn btn-secondary"
              disabled={loading}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? 'Caricamento...' : 'Carica'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AssetDocumentModal;
